import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useSite } from '../context/SiteContext';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import { IconMapPin, IconClock, IconWhatsapp } from '../components/Icons';
import styles from './Events.module.css';

const FEATURES = [
  { icon: '🎯', text: 'Tailored games for all group sizes' },
  { icon: '🤝', text: 'Builds trust, teamwork & communication' },
  { icon: '📍', text: 'Unique locations — desert, sea, forest, stables' },
  { icon: '😄', text: 'Safe, inclusive atmosphere for everyone' },
];

export default function Experiences() {
  const { data } = useSite();
  const navigate = useNavigate();
  const allPublished = (data.events || []).filter(e => e.published);
  const upcomingList = allPublished.filter(e => e.upcoming);
  const allList = allPublished;
  const whatsappPhone = (data.contact?.whatsapp || data.contact?.phone || '+962776113530').replace(/[^0-9]/g, '');

  return (
    <>
      <SEO
        title="Outdoor Team Building Experiences in Jordan | Keepers"
        description="Build stronger teams through unique outdoor experiences. Team games, desert adventures, and unforgettable moments in Jordan."
        image="https://keepersexperience.com/full_logo.png"
        url="https://keepersexperience.com/experiences"
        type="website"
      />
      <div className={styles.page}>
      <Navbar />

      {/* ─── HERO ─── */}
      <section className={styles.hero}>
        <div className={styles.heroOverlay} />
        <div className={styles.heroContent}>
          <motion.span
            className={styles.heroLabel}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45 }}
          >
            Unique Experiences
          </motion.span>
          <motion.h1
            className={styles.heroTitle}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.08 }}
          >
            Play, Connect,<br />and Laugh Anywhere
          </motion.h1>
          <motion.p
            className={styles.heroDesc}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.15 }}
          >
            At Keepers Experience, we believe that play is powerful. Our experiences are designed to bring people together — whether at the sea, in the desert, inside a stable, or under the forest canopy.
          </motion.p>
          <motion.div
            className={styles.heroFeatures}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.25 }}
          >
            {FEATURES.map((f, i) => (
              <div key={i} className={styles.featureItem}>
                <span className={styles.featureIcon}>{f.icon}</span>
                <span>{f.text}</span>
              </div>
            ))}
          </motion.div>
          <motion.p
            className={styles.heroCta}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.4, delay: 0.35 }}
          >
            Play anywhere. Laugh everywhere. Connect deeply.
          </motion.p>
        </div>
      </section>

      {/* ─── UPCOMING EXPERIENCES ─── */}
      {upcomingList.length > 0 && (
        <section className={styles.upcomingSection}>
          <div className={styles.container}>
            <div className={styles.sectionTop}>
              <div>
                <span className={styles.sectionLabel}>Don't miss out</span>
                <h2 className={styles.sectionTitle}>Upcoming Experiences</h2>
              </div>
            </div>

            <div className={styles.upcomingList}>
              {upcomingList.map((event, i) => (
                <motion.div
                  key={event.id}
                  className={styles.upcomingCard}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.1 }}
                  onClick={() => navigate(`/experiences/${event.slug}`)}
                >
                  <div className={styles.upcomingImg}>
                    <img src={event.image} alt={event.title} />
                    <div className={styles.upcomingImgOverlay} />
                    <span className={styles.upcomingBadge}>🗓 Upcoming</span>
                  </div>
                  <div className={styles.upcomingBody}>
                    <div className={styles.eventTags}>
                      {(event.tags || []).map(tag => (
                        <span key={tag} className={styles.tag}>{tag}</span>
                      ))}
                    </div>
                    <h3 className={styles.upcomingTitle}>{event.title}</h3>
                    <p className={styles.upcomingDesc}>{event.description}</p>
                    <div className={styles.eventMeta}>
                      <span className={styles.metaItem}><IconMapPin size={14} />{event.location}</span>
                      <span className={styles.metaItem}><IconClock size={14} />{event.date} · {event.time}</span>
                      {event.groupSize && <span className={styles.metaItem}>👥 {event.groupSize}</span>}
                    </div>
                    <div className={styles.upcomingFooter}>
                      <span className={styles.eventFee}>{event.fees}</span>
                      <div className={styles.upcomingActions}>
                        <button
                          className={styles.detailsBtn}
                          onClick={e => { e.stopPropagation(); navigate(`/experiences/${event.slug}`); }}
                        >
                          View Details
                        </button>
                        <button
                          className={styles.bookBtn}
                          onClick={e => {
                            e.stopPropagation();
                            const msg = encodeURIComponent(`Hi! I'd like to book a spot for: ${event.title}`);
                            window.open(`https://wa.me/${whatsappPhone}?text=${msg}`, '_blank');
                          }}
                        >
                          <IconWhatsapp size={16} /> Book via WhatsApp
                        </button>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ─── ALL EXPERIENCES ─── */}
      <section className={styles.allSection}>
        <div className={styles.container}>
          <div className={styles.sectionTop}>
            <div>
              <span className={styles.sectionLabel}>Explore everything</span>
              <h2 className={styles.sectionTitle}>All Experiences</h2>
            </div>
          </div>

          {allList.length === 0 ? (
            <p className={styles.empty}>No experiences yet. Check back soon!</p>
          ) : (
            <div className={styles.allGrid}>
              {allList.map((event, i) => (
                <motion.div
                  key={event.id}
                  className={styles.card}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.07 }}
                  onClick={() => navigate(`/experiences/${event.slug}`)}
                >
                  <div className={styles.cardImg}>
                    <img src={event.image} alt={event.title} loading="lazy" />
                    <div className={styles.cardImgOverlay} />
                    {event.upcoming && (
                      <span className={styles.cardUpcomingBadge}>🗓 Upcoming</span>
                    )}
                    <div className={styles.cardTags}>
                      {(event.tags || []).map(tag => (
                        <span key={tag} className={styles.tag}>{tag}</span>
                      ))}
                    </div>
                  </div>
                  <div className={styles.cardBody}>
                    <h3 className={styles.cardTitle}>{event.title}</h3>
                    <p className={styles.cardDesc}>{event.description}</p>
                    <div className={styles.eventMeta}>
                      <span className={styles.metaItem}><IconMapPin size={13} />{event.location}</span>
                      <span className={styles.metaItem}><IconClock size={13} />{event.date}</span>
                    </div>
                    <div className={styles.cardFooter}>
                      <span className={styles.eventFee}>{event.fees}</span>
                      <span className={styles.viewLink}>View →</span>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}

          {/* Bottom CTA */}
          <div className={styles.bottomCta}>
            <p className={styles.bottomCtaText}>Because at Keepers, fun isn't just a game — it's an experience.</p>
            <a
              href={`https://wa.me/${whatsappPhone}?text=${encodeURIComponent('Hi! I\'d like to know more about your experiences.')}`}
              target="_blank"
              rel="noreferrer"
              className={styles.bookBtn}
              style={{ display: 'inline-flex', textDecoration: 'none' }}
            >
              <IconWhatsapp size={16} /> Ask Us on WhatsApp
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
    </>
  );
}

